<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import { formatFileSize } from '@shared/utils'

// One order document (receipt, title deed): icon, name, "PDF • 344 KB" once
// the size is known. The whole row opens the preview.

interface Props {
  label: string
  icon: PiIconName
  sizeBytes: number | null
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
})

const emit = defineEmits<{
  (e: 'open'): void
}>()

const meta = computed(() => {
  const size = formatFileSize(props.sizeBytes)
  return size ? `PDF • ${size}` : 'PDF'
})
</script>

<template>
  <Box
    is="button"
    type="button"
    align="center"
    justify="between"
    gap="3"
    class="odr"
    :disabled="isLoading"
    :aria-busy="isLoading"
    :aria-label="`View ${label} (PDF)`"
    @click="emit('open')"
  >
    <Box align="center" gap="3" class="odr__main">
      <Box align="center" justify="center" class="odr__tile">
        <PiIcon :name="icon" />
      </Box>
      <Box direction="col" gap="1" align="start" class="odr__text">
        <Text type="body" weight="bold" is="span" truncate>{{ label }}</Text>
        <Text type="caption" weight="medium" color="muted" is="span">{{ meta }}</Text>
      </Box>
    </Box>
    <PiIcon
      :name="isLoading ? 'spinner' : 'eye'"
      class="odr__action"
      :class="{ 'pi-spin': isLoading }"
      aria-hidden="true"
    />
  </Box>
</template>

<style scoped>
.odr {
  width: 100%;
  min-height: 64px;
  padding: 0.75rem 1rem;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.odr:hover:not(:disabled) {
  background: var(--p-surface-50);
  border-color: var(--p-primary-200);
}

.odr:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.odr:disabled {
  cursor: progress;
}

.odr__main,
.odr__text {
  min-width: 0;
}

.odr__tile {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-50);
  color: var(--p-primary-600);
}

.odr__action {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--p-primary-600);
}
</style>
